import {
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AppLayout from '@/components/feature/AppLayout';

import {
  Drawer,
  EmptyState,
  LoadingState,
  SearchInput,
  useToast,
} from '@/components/base';

import {
  useRolePermissions,
} from '@/hooks/useRolePermissions';

import {
  useLegalProducts,
} from '@/hooks/useLegalProducts';

import {
  useDocumentTemplates,
  type DocumentTemplate,
  type DocumentTemplateType,
} from '@/hooks/useDocumentTemplates';

import {
  DOCUMENT_VARIABLE_CATALOG,
  DOCUMENT_VARIABLE_CATALOG_BY_KEY,
  DOCUMENT_VARIABLE_GROUP_LABELS,
  buildVariableDefinitions,
  extractDocumentVariables,
  getUnknownDocumentVariables,
  normalizeDocumentTags,
  type DocumentVariableDefinition,
  type DocumentVariableGroup,
} from '@/lib/documentTemplateVariables';

const documentTypeLabels:
  Record<
    DocumentTemplateType,
    string
  > = {
    contract:
      'Contrato',

    power_of_attorney:
      'Procuração',

    declaration:
      'Declaração',

    proposal:
      'Proposta',

    petition:
      'Petição',

    defense:
      'Defesa',

    appeal:
      'Recurso',

    notice:
      'Notificação',

    other:
      'Outro',
  };

const documentTypeOptions =
  (
    Object.keys(
      documentTypeLabels
    ) as
      DocumentTemplateType[]
  ).map(
    (value) => ({
      value,
      label:
        documentTypeLabels[
          value
        ],
    })
  );

const statusLabels = {
  active:
    'Ativo',

  archived:
    'Arquivado',
} as const;

const statusClasses = {
  active:
    'bg-emerald-50 text-emerald-700 border-emerald-200',

  archived:
    'bg-gray-100 text-gray-500 border-gray-200',
} as const;

type TemplateStatusFilter =
  | 'all'
  | 'active'
  | 'archived';

interface TemplateFormState {
  name: string;
  slug: string;

  documentType:
    DocumentTemplateType;

  legalProductId:
    string;

  category:
    string;

  subcategory:
    string;

  legalArea:
    string;

  purpose:
    string;

  description:
    string;

  content:
    string;

  tagsText:
    string;

  requiredVariableKeys:
    string[];

  variableDefinitions:
    DocumentVariableDefinition[];

  notes:
    string;

  isActive:
    boolean;

  isFavorite:
    boolean;
}

const emptyFormState =
  (): TemplateFormState => ({
    name: '',
    slug: '',

    documentType:
      'contract',

    legalProductId:
      '',

    category:
      '',

    subcategory:
      '',

    legalArea:
      '',

    purpose:
      '',

    description:
      '',

    content:
      '',

    tagsText:
      '',

    requiredVariableKeys:
      [],

    variableDefinitions:
      [],

    notes:
      '',

    isActive:
      true,

    isFavorite:
      false,
  });

const toNullableText = (
  value: string
) => {
  const text =
    value.trim();

  return text === ''
    ? null
    : text;
};

const formatDate = (
  value:
    string |
    null |
    undefined
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(
      value.includes('T')
        ? value
        : `${value}T00:00:00`
    );

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleDateString(
      'pt-BR'
    );
};

const formatDateTime = (
  value:
    string |
    null |
    undefined
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(value);

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleString(
      'pt-BR',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        year:
          'numeric',

        hour:
          '2-digit',

        minute:
          '2-digit',
      }
    );
};

const getDocumentTypeLabel =
  (
    value:
      DocumentTemplateType
  ) =>
    documentTypeLabels[
      value
    ] ?? value;

const buildFormStateFromTemplate =
  (
    template:
      DocumentTemplate
  ): TemplateFormState => ({
    name:
      template.name,

    slug:
      template.slug ??
      '',

    documentType:
      template.documentType,

    legalProductId:
      template.legalProductId ??
      '',

    category:
      template.category ??
      '',

    subcategory:
      template.subcategory ??
      '',

    legalArea:
      template.legalArea ??
      '',

    purpose:
      template.purpose ??
      '',

    description:
      template.description ??
      '',

    content:
      template.content,

    tagsText:
      template.tags.join(
        ', '
      ),

    requiredVariableKeys:
      template.variablesSchema
        .filter(
          (item) =>
            item.required
        )
        .map(
          (item) =>
            item.key
        ),

    variableDefinitions:
      template.variablesSchema,

    notes:
      template.notes ??
      '',

    isActive:
      template.isActive,

    isFavorite:
      template.isFavorite,
  });

const TemplatesPage:
  FC = () => {
    const {
      canManageLegalStructures,
    } =
      useRolePermissions();

    const {
      addToast,
    } =
      useToast();

    const {
      documentTemplates,

      isLoadingDocumentTemplates,

      documentTemplatesError,

      loadDocumentTemplates,

      createDocumentTemplate,

      updateDocumentTemplate,

      deleteDocumentTemplate,
    } =
      useDocumentTemplates({
        autoLoad:
          false,
      });

    const {
      legalProducts,
    } =
      useLegalProducts();

    const [
      search,
      setSearch,
    ] =
      useState('');

    const [
      typeFilter,
      setTypeFilter,
    ] =
      useState<
        DocumentTemplateType |
        ''
      >('');

    const [
      statusFilter,
      setStatusFilter,
    ] =
      useState<
        TemplateStatusFilter
      >('active');

    const [
      categoryFilter,
      setCategoryFilter,
    ] =
      useState('all');

    const [
      legalAreaFilter,
      setLegalAreaFilter,
    ] =
      useState('all');

    const [
      favoriteOnly,
      setFavoriteOnly,
    ] =
      useState(false);

    const [
      selectedTemplateId,
      setSelectedTemplateId,
    ] =
      useState<
        string | null
      >(null);

    const [
      editingTemplate,
      setEditingTemplate,
    ] =
      useState<
        DocumentTemplate |
        null
      >(null);

    const [
      editorOpen,
      setEditorOpen,
    ] =
      useState(false);

    const [
      form,
      setForm,
    ] =
      useState<
        TemplateFormState
      >(
        emptyFormState
      );

    const [
      isSavingTemplate,
      setIsSavingTemplate,
    ] =
      useState(false);

    const [
      archivingTemplateId,
      setArchivingTemplateId,
    ] =
      useState<
        string | null
      >(null);

    const [
      duplicatingTemplateId,
      setDuplicatingTemplateId,
    ] =
      useState<
        string | null
      >(null);

    const [
      favoritingTemplateId,
      setFavoritingTemplateId,
    ] =
      useState<
        string | null
      >(null);

    const [
      variableSearch,
      setVariableSearch,
    ] =
      useState('');

    const contentRef =
      useRef<
        HTMLTextAreaElement |
        null
      >(null);

    useEffect(
      () => {
        void loadDocumentTemplates({
          includeArchived:
            true,
        });
      },
      [
        loadDocumentTemplates,
      ]
    );

    const activeLegalProducts =
      useMemo(
        () =>
          legalProducts
            .filter(
              (product) =>
                product.isActive
            )
            .slice()
            .sort(
              (a, b) =>
                a.name.localeCompare(
                  b.name,
                  'pt-BR'
                )
            ),
        [
          legalProducts,
        ]
      );

    const selectedTemplate =
      useMemo(
        () =>
          documentTemplates.find(
            (template) =>
              template.id ===
              selectedTemplateId
          ) ?? null,
        [
          documentTemplates,
          selectedTemplateId,
        ]
      );

    const categories =
      useMemo(
        () =>
          Array.from(
            new Set(
              documentTemplates
                .map(
                  (template) =>
                    template.category
                )
                .filter(
                  (
                    value
                  ): value is string =>
                    Boolean(value)
                )
            )
          ).sort(
            (a, b) =>
              a.localeCompare(
                b,
                'pt-BR'
              )
          ),
        [
          documentTemplates,
        ]
      );

    const legalAreas =
      useMemo(
        () =>
          Array.from(
            new Set(
              documentTemplates
                .map(
                  (template) =>
                    template.legalArea
                )
                .filter(
                  (
                    value
                  ): value is string =>
                    Boolean(value)
                )
            )
          ).sort(
            (a, b) =>
              a.localeCompare(
                b,
                'pt-BR'
              )
          ),
        [
          documentTemplates,
        ]
      );

    const filteredTemplates =
      useMemo(
        () =>
          documentTemplates.filter(
            (template) => {
              if (
                statusFilter !==
                  'all' &&
                template.status !==
                  statusFilter
              ) {
                return false;
              }

              if (
                typeFilter &&
                template.documentType !==
                  typeFilter
              ) {
                return false;
              }

              if (
                categoryFilter !==
                  'all' &&
                template.category !==
                  categoryFilter
              ) {
                return false;
              }

              if (
                legalAreaFilter !==
                  'all' &&
                template.legalArea !==
                  legalAreaFilter
              ) {
                return false;
              }

              if (
                favoriteOnly &&
                !template.isFavorite
              ) {
                return false;
              }

              if (search) {
                const text =
                  search
                    .trim()
                    .toLowerCase();

                const haystack =
                  [
                    template.name,
                    template.slug ??
                      '',
                    template.category ??
                      '',
                    template.subcategory ??
                      '',
                    template.legalArea ??
                      '',
                    template.purpose ??
                      '',
                    template.description ??
                      '',
                    template.content,
                    template.notes ??
                      '',
                    template.legalProductName ??
                      '',
                    template.documentType,
                    ...template.tags,
                    ...template.variables,
                  ]
                    .join(' ')
                    .toLowerCase();

                if (
                  !haystack.includes(
                    text
                  )
                ) {
                  return false;
                }
              }

              return true;
            }
          ),
        [
          categoryFilter,
          documentTemplates,
          favoriteOnly,
          legalAreaFilter,
          search,
          statusFilter,
          typeFilter,
        ]
      );

    const usedVariables =
      useMemo(
        () =>
          extractDocumentVariables(
            form.content
          ),
        [
          form.content,
        ]
      );

    const unknownVariables =
      useMemo(
        () =>
          getUnknownDocumentVariables(
            usedVariables
          ),
        [
          usedVariables,
        ]
      );

    const editorVariableDefinitions =
      useMemo(
        () =>
          buildVariableDefinitions(
            usedVariables,
            form.variableDefinitions,
            form.requiredVariableKeys
          ),
        [
          form.requiredVariableKeys,
          form.variableDefinitions,
          usedVariables,
        ]
      );

    const filteredVariableCatalog =
      useMemo(
        () => {
          const query =
            variableSearch
              .trim()
              .toLowerCase();

          if (!query) {
            return DOCUMENT_VARIABLE_CATALOG;
          }

          return DOCUMENT_VARIABLE_CATALOG.filter(
            (item) =>
              [
                item.key,
                item.label,
                DOCUMENT_VARIABLE_GROUP_LABELS[
                  item.group
                ],
              ]
                .join(' ')
                .toLowerCase()
                .includes(query)
          );
        },
        [
          variableSearch,
        ]
      );

    const groupedVariableCatalog =
      useMemo(
        () => {
          const groups =
            new Map<
              DocumentVariableGroup,
              typeof DOCUMENT_VARIABLE_CATALOG
            >();

          for (
            const item of
              filteredVariableCatalog
          ) {
            const list =
              groups.get(
                item.group
              ) ?? [];

            list.push(item);

            groups.set(
              item.group,
              list
            );
          }

          return Array.from(
            groups.entries()
          );
        },
        [
          filteredVariableCatalog,
        ]
      );

    const openCreateDrawer =
      () => {
        setEditingTemplate(
          null
        );

        setForm(
          emptyFormState()
        );

        setVariableSearch(
          ''
        );

        setEditorOpen(
          true
        );
      };

    const openEditDrawer =
      (
        template:
          DocumentTemplate
      ) => {
        setEditingTemplate(
          template
        );

        setForm(
          buildFormStateFromTemplate(
            template
          )
        );

        setVariableSearch(
          ''
        );

        setEditorOpen(
          true
        );
      };

    const closeEditor =
      () => {
        setEditorOpen(
          false
        );

        setEditingTemplate(
          null
        );

        setForm(
          emptyFormState()
        );

        setVariableSearch(
          ''
        );
      };

    const insertVariable =
      (
        key: string
      ) => {
        const token =
          `{{${key}}}`;

        const textarea =
          contentRef.current;

        if (!textarea) {
          setForm(
            (prev) => ({
              ...prev,
              content:
                `${prev.content}${prev.content ? '\n' : ''}${token}`,
            })
          );

          return;
        }

        const start =
          textarea.selectionStart;

        const end =
          textarea.selectionEnd;

        setForm(
          (prev) => ({
            ...prev,

            content:
              prev.content.slice(
                0,
                start
              ) +
              token +
              prev.content.slice(
                end
              ),
          })
        );

        requestAnimationFrame(
          () => {
            textarea.focus();

            const cursor =
              start +
              token.length;

            textarea.setSelectionRange(
              cursor,
              cursor
            );
          }
        );
      };

    const toggleRequiredVariable =
      (
        key: string
      ) => {
        setForm(
          (prev) => {
            const exists =
              prev.requiredVariableKeys.includes(
                key
              );

            return {
              ...prev,

              requiredVariableKeys:
                exists
                  ? prev.requiredVariableKeys.filter(
                      (item) =>
                        item !==
                        key
                    )
                  : [
                      ...prev.requiredVariableKeys,
                      key,
                    ],
            };
          }
        );
      };

    const saveTemplate =
      async () => {
        if (
          isSavingTemplate
        ) {
          return;
        }

        if (
          !form.name.trim()
        ) {
          addToast(
            'Preencha o nome do modelo.',
            'warning'
          );

          return;
        }

        if (
          !form.content.trim()
        ) {
          addToast(
            'Preencha o conteúdo do modelo.',
            'warning'
          );

          return;
        }

        setIsSavingTemplate(
          true
        );

        const variables =
          extractDocumentVariables(
            form.content
          );

        const variablesSchema =
          buildVariableDefinitions(
            variables,
            editorVariableDefinitions,
            form.requiredVariableKeys
          );

        const payload = {
          name:
            form.name.trim(),

          slug:
            form.slug.trim() ||
            undefined,

          documentType:
            form.documentType,

          legalProductId:
            form.legalProductId.trim() ||
            null,

          category:
            toNullableText(
              form.category
            ),

          subcategory:
            toNullableText(
              form.subcategory
            ),

          legalArea:
            toNullableText(
              form.legalArea
            ),

          purpose:
            toNullableText(
              form.purpose
            ),

          description:
            toNullableText(
              form.description
            ),

          content:
            form.content.trim(),

          variables,

          variablesSchema,

          tags:
            normalizeDocumentTags(
              form.tagsText
            ),

          isFavorite:
            form.isFavorite,

          isActive:
            form.isActive,

          notes:
            toNullableText(
              form.notes
            ),

          changeNote:
            editingTemplate
              ? 'Atualização pela interface de modelos'
              : undefined,
        };

        try {
          const saved =
            editingTemplate
              ? await updateDocumentTemplate(
                  editingTemplate.id,
                  payload
                )
              : await createDocumentTemplate(
                  payload
                );

          setSelectedTemplateId(
            saved.id
          );

          addToast(
            editingTemplate
              ? `Modelo atualizado. Versão atual: ${saved.currentVersion}.`
              : 'Modelo criado com sucesso.',
            'success'
          );

          closeEditor();
        } catch (
          error: unknown
        ) {
          addToast(
            error instanceof
              Error
              ? error.message
              : 'Não foi possível salvar o modelo.',
            'error'
          );
        } finally {
          setIsSavingTemplate(
            false
          );
        }
      };

    const toggleFavorite =
      async (
        template:
          DocumentTemplate
      ) => {
        if (
          favoritingTemplateId ||
          !canManageLegalStructures
        ) {
          return;
        }

        setFavoritingTemplateId(
          template.id
        );

        try {
          await updateDocumentTemplate(
            template.id,
            {
              isFavorite:
                !template.isFavorite,
            }
          );
        } catch (
          error: unknown
        ) {
          addToast(
            error instanceof
              Error
              ? error.message
              : 'Não foi possível atualizar o favorito.',
            'error'
          );
        } finally {
          setFavoritingTemplateId(
            null
          );
        }
      };

    const duplicateTemplate =
      async (
        template:
          DocumentTemplate
      ) => {
        if (
          duplicatingTemplateId ||
          !canManageLegalStructures
        ) {
          return;
        }

        setDuplicatingTemplateId(
          template.id
        );

        try {
          const created =
            await createDocumentTemplate({
              name:
                `${template.name} — Cópia`,

              documentType:
                template.documentType,

              legalProductId:
                template.legalProductId,

              category:
                template.category,

              subcategory:
                template.subcategory,

              legalArea:
                template.legalArea,

              purpose:
                template.purpose,

              description:
                template.description,

              content:
                template.content,

              variables:
                template.variables,

              variablesSchema:
                template.variablesSchema,

              tags:
                template.tags,

              notes:
                template.notes,

              isFavorite:
                false,

              isActive:
                true,
            });

          setSelectedTemplateId(
            created.id
          );

          addToast(
            'Modelo duplicado com sucesso.',
            'success'
          );
        } catch (
          error: unknown
        ) {
          addToast(
            error instanceof
              Error
              ? error.message
              : 'Não foi possível duplicar o modelo.',
            'error'
          );
        } finally {
          setDuplicatingTemplateId(
            null
          );
        }
      };

    const archiveTemplate =
      async (
        template:
          DocumentTemplate
      ) => {
        if (
          isSavingTemplate ||
          archivingTemplateId
        ) {
          return;
        }

        const confirmed =
          window.confirm(
            'Tem certeza que deseja arquivar este modelo? O histórico e as versões serão preservados.'
          );

        if (!confirmed) {
          return;
        }

        setArchivingTemplateId(
          template.id
        );

        try {
          await deleteDocumentTemplate(
            template.id
          );

          if (
            selectedTemplateId ===
            template.id
          ) {
            setSelectedTemplateId(
              null
            );
          }

          addToast(
            'Modelo arquivado com sucesso.',
            'success'
          );

          await loadDocumentTemplates({
            includeArchived:
              true,
          });
        } catch (
          error: unknown
        ) {
          addToast(
            error instanceof
              Error
              ? error.message
              : 'Não foi possível arquivar o modelo.',
            'error'
          );
        } finally {
          setArchivingTemplateId(
            null
          );
        }
      };

    const linkedProductMissing =
      Boolean(
        form.legalProductId
      ) &&
      !activeLegalProducts.some(
        (product) =>
          product.id ===
          form.legalProductId
      );

    return (
      <AppLayout>
        <div className="p-4 md:p-6 space-y-5">

          {!canManageLegalStructures ? (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
              Modo consulta: seu perfil pode visualizar este módulo, mas alterações estruturais são reservadas a administradores e advogados.
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground-900">
                  Modelos de Documentos
                </h2>

                <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-[10px] font-semibold">
                  Inteligente
                </span>
              </div>

              <p className="text-sm text-foreground-500 mt-0.5">
                {filteredTemplates.length} modelo(s) · classificação, variáveis e versionamento
              </p>
            </div>

            <button
              hidden={!canManageLegalStructures}
              onClick={openCreateDrawer}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Novo Modelo
            </button>
          </div>

          <div className="space-y-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Buscar por nome, categoria, área, tag, variável..."
              className="sm:w-96"
            />

            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                  statusFilter === 'active'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600'
                }`}
              >
                Ativos
              </button>

              <button
                onClick={() => setStatusFilter('archived')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                  statusFilter === 'archived'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600'
                }`}
              >
                Arquivados
              </button>

              <button
                onClick={() => setStatusFilter('all')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                  statusFilter === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600'
                }`}
              >
                Todos
              </button>

              <button
                onClick={() => setFavoriteOnly((prev) => !prev)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                  favoriteOnly
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-background-100 text-foreground-600'
                }`}
              >
                <i className={`${favoriteOnly ? 'ri-star-fill' : 'ri-star-line'} mr-1`}></i>
                Favoritos
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select
                value={typeFilter}
                onChange={(event) =>
                  setTypeFilter(event.target.value as DocumentTemplateType | '')
                }
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="">
                  Todos os tipos
                </option>

                {documentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="all">
                  Todas as categorias
                </option>

                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              <select
                value={legalAreaFilter}
                onChange={(event) => setLegalAreaFilter(event.target.value)}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                <option value="all">
                  Todas as áreas jurídicas
                </option>

                {legalAreas.map((legalArea) => (
                  <option key={legalArea} value={legalArea}>
                    {legalArea}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {documentTemplatesError && (
            <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-amber-700">
                <i className="ri-alert-line"></i>
                <span>{documentTemplatesError}</span>
              </div>

              <button
                onClick={() =>
                  void loadDocumentTemplates({
                    includeArchived: true,
                  })
                }
                className="text-xs font-medium text-amber-700 hover:text-amber-800 cursor-pointer"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {isLoadingDocumentTemplates ? (
            <LoadingState message="Carregando modelos operacionais..." />
          ) : filteredTemplates.length === 0 ? (
            <EmptyState
              icon="ri-file-copy-line"
              title="Nenhum modelo encontrado"
              description="Ajuste os filtros ou crie um novo modelo documental."
              action={
                canManageLegalStructures
                  ? {
                      label: 'Novo Modelo',
                      onClick: openCreateDrawer,
                      icon: 'ri-add-line',
                    }
                  : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`bg-background-50 border rounded-xl p-4 cursor-pointer transition-all group ${
                    selectedTemplateId === template.id
                      ? 'border-primary-300 ring-1 ring-primary-100'
                      : 'border-background-200 hover:border-primary-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 font-medium">
                        {getDocumentTypeLabel(template.documentType)}
                      </span>

                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusClasses[template.status]}`}>
                        {statusLabels[template.status]}
                      </span>

                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">
                        v{template.currentVersion}
                      </span>
                    </div>

                    <button
                      hidden={!canManageLegalStructures}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void toggleFavorite(template);
                      }}
                      disabled={favoritingTemplateId === template.id}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 cursor-pointer disabled:opacity-50"
                      title={template.isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <i className={`${template.isFavorite ? 'ri-star-fill text-amber-500' : 'ri-star-line text-foreground-400'}`}></i>
                    </button>
                  </div>

                  <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
                    {template.name}
                  </h4>

                  <p className="text-xs text-foreground-400 mt-1">
                    {template.legalProductName ?? 'Sem produto jurídico vinculado'}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {template.category && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] bg-blue-50 text-blue-700">
                        {template.category}
                      </span>
                    )}

                    {template.legalArea && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] bg-emerald-50 text-emerald-700">
                        {template.legalArea}
                      </span>
                    )}

                    {template.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md text-[10px] bg-background-100 text-foreground-600"
                      >
                        #{tag}
                      </span>
                    ))}

                    {template.tags.length > 3 && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] bg-background-100 text-foreground-500">
                        +{template.tags.length - 3}
                      </span>
                    )}
                  </div>

                  {template.description && (
                    <p className="text-xs text-foreground-500 mt-3 line-clamp-3">
                      {template.description}
                    </p>
                  )}

                  <div className="mt-3 text-[11px] text-foreground-400">
                    {template.variables.length} variável(is) · atualizado em {formatDate(template.updatedAt)}
                  </div>

                  <div className="flex flex-wrap items-center gap-1 mt-3 pt-3 border-t border-background-100">
                    <button
                      hidden={!canManageLegalStructures}
                      onClick={(event) => {
                        event.stopPropagation();
                        openEditDrawer(template);
                      }}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-background-100 text-foreground-600 hover:bg-primary-50 cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Editar
                    </button>

                    <button
                      hidden={!canManageLegalStructures}
                      onClick={(event) => {
                        event.stopPropagation();
                        void duplicateTemplate(template);
                      }}
                      disabled={duplicatingTemplateId === template.id}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-background-100 text-foreground-600 hover:bg-violet-50 cursor-pointer disabled:opacity-60"
                    >
                      <i className="ri-file-copy-line mr-1"></i>
                      {duplicatingTemplateId === template.id ? 'Duplicando...' : 'Duplicar'}
                    </button>

                    {template.status !== 'archived' && (
                      <button
                        hidden={!canManageLegalStructures}
                        onClick={(event) => {
                          event.stopPropagation();
                          void archiveTemplate(template);
                        }}
                        disabled={archivingTemplateId === template.id}
                        className="px-2 py-1 rounded text-[10px] font-medium bg-background-100 text-foreground-600 hover:bg-red-50 cursor-pointer disabled:opacity-60"
                      >
                        <i className="ri-archive-line mr-1"></i>
                        {archivingTemplateId === template.id ? 'Arquivando...' : 'Arquivar'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedTemplate && (
            <div className="bg-background-50 border border-background-200 rounded-xl p-5">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-5">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground-900">
                      {selectedTemplate.name}
                    </h3>

                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700">
                      Versão {selectedTemplate.currentVersion}
                    </span>

                    {selectedTemplate.isFavorite && (
                      <i className="ri-star-fill text-amber-500" title="Favorito"></i>
                    )}
                  </div>

                  <p className="text-xs text-foreground-500 mt-1">
                    {getDocumentTypeLabel(selectedTemplate.documentType)} · {selectedTemplate.slug ?? 'Sem slug'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <button
                    hidden={!canManageLegalStructures}
                    onClick={() => openEditDrawer(selectedTemplate)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-primary-50 cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    Editar
                  </button>

                  <button
                    hidden={!canManageLegalStructures}
                    onClick={() => void duplicateTemplate(selectedTemplate)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-violet-50 cursor-pointer"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    Duplicar
                  </button>

                  <button
                    onClick={() => setSelectedTemplateId(null)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Categoria
                  </span>
                  <p className="text-sm text-foreground-700">
                    {selectedTemplate.category ?? '—'}
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Subcategoria
                  </span>
                  <p className="text-sm text-foreground-700">
                    {selectedTemplate.subcategory ?? '—'}
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Área jurídica
                  </span>
                  <p className="text-sm text-foreground-700">
                    {selectedTemplate.legalArea ?? '—'}
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Finalidade
                  </span>
                  <p className="text-sm text-foreground-700">
                    {selectedTemplate.purpose ?? '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Tipo
                  </span>
                  <p className="text-sm text-foreground-700">
                    {getDocumentTypeLabel(selectedTemplate.documentType)}
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Produto jurídico
                  </span>
                  <p className="text-sm text-foreground-700">
                    {selectedTemplate.legalProductName ?? 'Sem vínculo'}
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400">
                    Status
                  </span>
                  <p className="text-sm text-foreground-700">
                    {statusLabels[selectedTemplate.status]}
                  </p>
                </div>
              </div>

              {selectedTemplate.tags.length > 0 && (
                <div className="mb-4">
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-2">
                    Tags
                  </span>

                  <div className="flex flex-wrap gap-1.5">
                    {selectedTemplate.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 rounded-md text-[10px] bg-background-100 text-foreground-600"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedTemplate.description && (
                <div className="mb-4">
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-1">
                    Descrição
                  </span>

                  <div className="px-3 py-2 border border-background-200 rounded-lg text-sm text-foreground-800">
                    {selectedTemplate.description}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-1">
                  Conteúdo
                </span>

                <textarea
                  readOnly
                  rows={10}
                  value={selectedTemplate.content}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-none"
                />
              </div>

              <div className="mb-4">
                <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-2">
                  Variáveis do modelo
                </span>

                {selectedTemplate.variables.length > 0 ? (
                  <div className="space-y-2">
                    {selectedTemplate.variables.map((key) => {
                      const configured =
                        selectedTemplate.variablesSchema.find(
                          (item) => item.key === key
                        );

                      const catalog =
                        DOCUMENT_VARIABLE_CATALOG_BY_KEY.get(key);

                      return (
                        <div
                          key={key}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 rounded-lg border border-background-200"
                        >
                          <div>
                            <p className="text-sm font-medium text-foreground-800">
                              {configured?.label ?? catalog?.label ?? key}
                            </p>

                            <p className="text-[11px] text-foreground-400 font-mono">
                              {`{{${key}}}`} · {DOCUMENT_VARIABLE_GROUP_LABELS[configured?.group ?? catalog?.group ?? 'manual']}
                            </p>
                          </div>

                          <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
                            configured?.required
                              ? 'bg-rose-50 text-rose-700'
                              : 'bg-background-100 text-foreground-500'
                          }`}>
                            {configured?.required ? 'Obrigatória' : 'Opcional'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400">
                    Nenhuma variável utilizada no conteúdo.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-400 mb-1">
                    Observações
                  </span>

                  <div className="px-3 py-2 border border-background-200 rounded-lg text-sm text-foreground-800 min-h-[80px] whitespace-pre-line">
                    {selectedTemplate.notes || 'Sem observações.'}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-foreground-500">
                      Versão atual
                    </span>
                    <strong>
                      {selectedTemplate.currentVersion}
                    </strong>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span className="text-foreground-500">
                      Criado em
                    </span>
                    <span>
                      {formatDateTime(selectedTemplate.createdAt)}
                    </span>
                  </div>

                  <div className="flex justify-between gap-3">
                    <span className="text-foreground-500">
                      Atualizado em
                    </span>
                    <span>
                      {formatDateTime(selectedTemplate.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Drawer
            isOpen={canManageLegalStructures && editorOpen}
            onClose={closeEditor}
            title={
              editingTemplate
                ? `Editar modelo: ${editingTemplate.name}`
                : 'Novo Modelo'
            }
            width="w-[960px]"
            footer={
              <div className="flex items-center gap-2">
                <button
                  onClick={closeEditor}
                  disabled={isSavingTemplate}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer disabled:opacity-60"
                >
                  Cancelar
                </button>

                <button
                  hidden={!canManageLegalStructures}
                  onClick={() => void saveTemplate()}
                  disabled={isSavingTemplate}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-60"
                >
                  {isSavingTemplate
                    ? 'Salvando...'
                    : editingTemplate
                      ? 'Salvar nova versão'
                      : 'Criar Modelo'}
                </button>
              </div>
            }
          >
            <div className="space-y-5">
              {editingTemplate && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 text-violet-700 text-xs">
                  <i className="ri-git-commit-line"></i>
                  Versão atual: <strong>{editingTemplate.currentVersion}</strong>.
                  Alterações documentais relevantes gerarão uma nova versão automaticamente.
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Nome *
                </label>

                <input
                  type="text"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Tipo *
                  </label>

                  <select
                    value={form.documentType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        documentType: event.target.value as DocumentTemplateType,
                      }))
                    }
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                  >
                    {documentTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Categoria
                  </label>

                  <input
                    type="text"
                    list="document-template-categories"
                    value={form.category}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        category: event.target.value,
                      }))
                    }
                    placeholder="Ex.: Contratos"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                  />

                  <datalist id="document-template-categories">
                    {categories.map((category) => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Subcategoria
                  </label>

                  <input
                    type="text"
                    value={form.subcategory}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        subcategory: event.target.value,
                      }))
                    }
                    placeholder="Ex.: Honorários"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Área jurídica
                  </label>

                  <input
                    type="text"
                    list="document-template-legal-areas"
                    value={form.legalArea}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        legalArea: event.target.value,
                      }))
                    }
                    placeholder="Ex.: Direito Penal"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                  />

                  <datalist id="document-template-legal-areas">
                    {legalAreas.map((legalArea) => (
                      <option key={legalArea} value={legalArea} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Finalidade
                  </label>

                  <input
                    type="text"
                    value={form.purpose}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        purpose: event.target.value,
                      }))
                    }
                    placeholder="Ex.: contratação inicial"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Slug
                  </label>

                  <input
                    type="text"
                    value={form.slug}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        slug: event.target.value,
                      }))
                    }
                    placeholder="Gerado automaticamente"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Produto jurídico
                </label>

                <select
                  value={form.legalProductId}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      legalProductId: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                >
                  <option value="">
                    Sem vínculo
                  </option>

                  {linkedProductMissing && form.legalProductId && (
                    <option value={form.legalProductId}>
                      Produto vinculado não encontrado
                    </option>
                  )}

                  {activeLegalProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Descrição
                </label>

                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm resize-none"
                />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.75fr)] gap-4">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="text-xs font-medium text-foreground-600">
                      Conteúdo do modelo *
                    </label>

                    <span className="text-[11px] text-foreground-400">
                      {usedVariables.length} variável(is) detectada(s)
                    </span>
                  </div>

                  <textarea
                    ref={contentRef}
                    value={form.content}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        content: event.target.value,
                      }))
                    }
                    rows={22}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 font-mono resize-y focus:outline-none focus:border-primary-300"
                    placeholder="Escreva o conteúdo. Use o catálogo ao lado para inserir variáveis."
                  />

                  {unknownVariables.length > 0 && (
                    <div className="mt-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
                      <div className="font-semibold mb-1">
                        Variáveis personalizadas detectadas
                      </div>

                      <p>
                        Elas serão permitidas, mas precisarão ser preenchidas manualmente quando o documento for gerado:
                        {' '}
                        {unknownVariables.map((key) => `{{${key}}}`).join(', ')}.
                      </p>
                    </div>
                  )}
                </div>

                <div className="border border-background-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-braces-line text-primary-600"></i>

                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800">
                        Catálogo de variáveis
                      </h4>

                      <p className="text-[11px] text-foreground-400">
                        Clique para inserir no cursor.
                      </p>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={variableSearch}
                    onChange={(event) => setVariableSearch(event.target.value)}
                    placeholder="Buscar variável..."
                    className="w-full px-3 py-2 mb-3 bg-background-50 border border-background-200 rounded-lg text-xs"
                  />

                  <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
                    {groupedVariableCatalog.map(([group, items]) => (
                      <div key={group}>
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-foreground-400 mb-1.5">
                          {DOCUMENT_VARIABLE_GROUP_LABELS[group]}
                        </div>

                        <div className="space-y-1">
                          {items.map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => insertVariable(item.key)}
                              className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-primary-50 transition-colors cursor-pointer"
                            >
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground-700 truncate">
                                  {item.label}
                                </p>

                                <p className="text-[10px] font-mono text-foreground-400 truncate">
                                  {`{{${item.key}}}`}
                                </p>
                              </div>

                              <i className="ri-add-line text-primary-500"></i>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className="text-xs font-medium text-foreground-600">
                    Variáveis utilizadas
                  </label>

                  <span className="text-[11px] text-foreground-400">
                    Marque as que não podem ficar sem valor na geração.
                  </span>
                </div>

                {editorVariableDefinitions.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {editorVariableDefinitions.map((definition) => (
                      <div
                        key={definition.key}
                        className="flex items-center justify-between gap-3 px-3 py-2 border border-background-200 rounded-lg"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground-800 truncate">
                            {definition.label}
                          </p>

                          <p className="text-[10px] text-foreground-400 font-mono truncate">
                            {`{{${definition.key}}}`} · {DOCUMENT_VARIABLE_GROUP_LABELS[definition.group]}
                          </p>
                        </div>

                        <label className="inline-flex items-center gap-1.5 text-[10px] text-foreground-600 cursor-pointer whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={form.requiredVariableKeys.includes(definition.key)}
                            onChange={() => toggleRequiredVariable(definition.key)}
                          />

                          Obrigatória
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-3 rounded-lg bg-background-100 text-xs text-foreground-500">
                    Nenhuma variável foi encontrada no conteúdo. Insira uma pelo catálogo ou digite no formato <span className="font-mono">{'{{variavel}}'}</span>.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Tags
                </label>

                <input
                  type="text"
                  value={form.tagsText}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tagsText: event.target.value,
                    }))
                  }
                  placeholder="Ex.: penal, procuração, cliente, urgente"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm"
                />

                <p className="text-[11px] text-foreground-400 mt-1">
                  Separe por vírgula. Duplicidades serão eliminadas automaticamente.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">
                  Observações internas
                </label>

                <textarea
                  value={form.notes}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Status
                  </label>

                  <select
                    value={form.isActive ? 'active' : 'archived'}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        isActive: event.target.value === 'active',
                      }))
                    }
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm cursor-pointer"
                  >
                    <option value="active">
                      Ativo
                    </option>

                    <option value="archived">
                      Arquivado
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    Destaque
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        isFavorite: !prev.isFavorite,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-lg text-sm text-left cursor-pointer ${
                      form.isFavorite
                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                        : 'border-background-200 bg-background-50 text-foreground-700'
                    }`}
                  >
                    <i className={`${form.isFavorite ? 'ri-star-fill' : 'ri-star-line'} mr-2`}></i>
                    {form.isFavorite ? 'Modelo favorito' : 'Marcar como favorito'}
                  </button>
                </div>
              </div>
            </div>
          </Drawer>
        </div>
      </AppLayout>
    );
  };

export default TemplatesPage;